import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AsyncPipe, NgIf, NgFor } from '@angular/common';
import { Store } from '@ngrx/store';
import {
  ApiService,
  CoreUiPartialState,
  MessageService,
  checkIsShowroomView,
  getArbCentralNavSecurity,
  getDefaultCustomer,
  getDisplayOptions,
  getIsShowroomView,
  getOpenOrder,
  getSecurityFunctions,
} from '@pos-app/core-ui';
import { DisplayOptions, ItemPriceGroup, OpenOrder, OrderCustomerPrice, SECURITY_FUNCTION } from '@pos-app/data';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, finalize, take, takeUntil } from 'rxjs/operators';
import { ItemBranchAvailability } from '../../../orders/+state/orders.models';
import { FuzzySearchComponent } from '@pos-app/core-ui/lib/components/fuzzy-search/fuzzy-search.component';
import { ItemPriceGroupResponse } from '@pos-app/core-ui/lib/models/item-price-group';
import { CoreUiModule } from '../../../../../../../../libs/core-ui/src/lib/core-ui.module';

@Component({
    selector: 'app-stockavailability',
    templateUrl: './stockavailability.component.html',
    styleUrls: ['./stockavailability.component.scss'],
    imports: [CoreUiModule, NgIf, NgFor, AsyncPipe]
})
export class StockavailabilityComponent implements OnInit, OnDestroy {
  @ViewChild('fuzzySearch') fuzzySearch: FuzzySearchComponent;

  @Input() isExternalUser: boolean;
  @Output() showStockAvailability = new EventEmitter<boolean>();

  private _isLoading$ = new BehaviorSubject<boolean>(false);
  isLoading$ = this._isLoading$.asObservable();
  unSubscribe$ = new Subject<void>();
  itemAvailability: ItemBranchAvailability[];
  itemPriceGroup: ItemPriceGroup;
  customer: any;
  displayOptions: DisplayOptions;
  allowToViewStock: boolean;
  isShowroomView: boolean;
  allowShowroomViewToggle: boolean;
  openOrder: OpenOrder;
  orderCustomerPrice: OrderCustomerPrice;
  isInternalUser: boolean;
  isShowTable = false;
  atpBranch: string;
  atpDateDescription: string;
  isDisplayMyPrice: boolean;
  itemNumber: string;

  constructor(
    private apiService: ApiService,
    private coreUiStore: Store<CoreUiPartialState>,
    private toastr: ToastrService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    combineLatest([
      this.coreUiStore.select(getDisplayOptions),
      this.coreUiStore.select(getDefaultCustomer),
      this.coreUiStore.select(getSecurityFunctions),
    ])
      .pipe(take(1))
      .subscribe(([displayOptions, defaultCustomer, securityFunctions]) => {
        this.displayOptions = displayOptions;
        this.customer = defaultCustomer;
        this.allowToViewStock = securityFunctions.indexOf(SECURITY_FUNCTION.viewStock) !== -1;
      });

    this.coreUiStore
      .select(getIsShowroomView)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => {
        this.isShowroomView = res;
      });

    this.coreUiStore
      .select(getArbCentralNavSecurity)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((securityFunctions) => {
        this.allowShowroomViewToggle = !!securityFunctions?.showroomViewActionTF;
      });

    this.coreUiStore
      .select(getOpenOrder)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((openOrder) => {
        this.openOrder = openOrder;
      });

    this.messageService
      .listen('userStateUpdate')
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(() => {
        if (this.itemNumber) {
          this.loadItemAvailability(this.itemNumber);
        }
      });
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  clearSearchValue() {
    this.itemAvailability = null;
    this.itemPriceGroup = null;
    this.orderCustomerPrice = null;
  }

  /* same behaviour as the Showroom View toggle under Preferences in the nav bar */
  toggleShowroom(showroomView: boolean) {
    this.apiService
      .toggleShowroomView({
        showroomViewYN: showroomView ? 'Y' : 'N',
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(() => {
        this.messageService.trigger('userStateUpdate');
        this.coreUiStore.dispatch(checkIsShowroomView({ isShowRoomView: showroomView }));
      });
  }

  cancel(): void {
    this.showStockAvailability.emit(false);
    this.isShowTable = false;
    this.fuzzySearch.clear();
  }

  loadItemAvailability(itemNumber) {
    this.isInternalUser = !this.isExternalUser;
    this.itemAvailability = null;
    this.itemPriceGroup = null;
    this.orderCustomerPrice = null;
    this._isLoading$.next(true);
    this.itemNumber = itemNumber;

    const canViewPrices = this.displayOptions.displayExTax || this.displayOptions.displayIncTax || this.isInternalUser;
    const orderCustomerNumber = this.openOrder?.orderNumber?.trim() ? this.openOrder.customerNumber : null;

    combineLatest([
      this.allowToViewStock || this.isInternalUser ? this.apiService.getItemAvailability({ itemNumber }) : of(null),
      canViewPrices
        ? this.apiService.getItemPriceGroup({
            customerNumber: this.customer.customerNumber,
            branch: this.customer.defaultBranch,
            itemCodes: [{ code: itemNumber, quantity: 1 }],
            postCode: '',
            overrideShowroomViewYN: 'N',
          })
        : of(null),
      /* customer's price for the open order; a failure here shouldn't stop the rest of the screen loading */
      canViewPrices && orderCustomerNumber
        ? this.apiService
            .getItemPriceGroup({
              customerNumber: orderCustomerNumber,
              branch: this.customer.defaultBranch,
              itemCodes: [{ code: itemNumber, quantity: 1 }],
              postCode: '',
              overrideShowroomViewYN: 'N',
            })
            .pipe(catchError(() => of(null)))
        : of(null),
    ])
      .pipe(
        finalize(() => this._isLoading$.next(false)),
        takeUntil(this.unSubscribe$)
      )
      .subscribe(
        ([itemAvailability, priceGroup, orderCustomerPriceGroup]: [any, ItemPriceGroupResponse, ItemPriceGroupResponse]) => {
          this._isLoading$.next(false);
          this.itemAvailability = itemAvailability ? itemAvailability.SearchResults : {};
          this.itemPriceGroup = priceGroup ? priceGroup?.items[0] : ({} as any);
          this.isShowTable = true;
          this.atpBranch = itemAvailability.ATPBranch;
          this.atpDateDescription = itemAvailability.ATPDateDescription;
          this.isDisplayMyPrice = priceGroup.displayMyPriceYN === 'Y';
          this.orderCustomerPrice = this.getOrderCustomerPrice(orderCustomerPriceGroup);

          this.showStockAvailability.emit(true);
        },
        (error) => {
          this.toastr.error(error.ErrorMessage);
        }
      );
  }

  private getOrderCustomerPrice(priceGroup: ItemPriceGroupResponse): OrderCustomerPrice {
    const item = priceGroup?.items?.[0];
    if (!item) {
      return null;
    }

    return {
      label: `Customer - ${this.openOrder?.customerName || priceGroup.customerName || ''}`,
      unitPrice: item.customerUnitPrice,
      taxedUnitPrice: item.customerTaxedUnitPrice,
      currency: item.customerCurrency,
    };
  }

  displayAvailability(availabilityNumber, availabilityString) {
    return isNaN(Number(availabilityString)) ? availabilityString : +availabilityNumber;
  }
}

